import { useEffect, useRef, useState } from 'react';
import { Grid, Typography, Tooltip } from '@mui/material';
import { theme } from 'theme';
import { jbColors } from 'theme/jbColors';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { GlobalState } from 'types/state';
import _ from 'lodash';
import { getProductsThunk } from 'thunks/productThunk';
import { Box } from '@mui/system';
import { SimpleSearch } from 'components/common/SimpleSearchField';
import { ProductCatalogueResponse } from 'pco-product-service-internal-consumer';
import { useNavigate } from 'react-router';
import AccordionGridWrapper from 'components/common/AccordionGridWrapper';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { createServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import StickyHeaderWrapper from 'components/common/StickyHeaderWrapper';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const styles = {
  divider: {
    flex: 1,
    borderColor: jbColors.core.reflexBlue[40],
  },
  productBox: {
    appearance: 'none',
    backgroundColor: 'transparent',
    padding: '1em',
    margin: '4px',
    minHeight: '10.2em',
    width: '100%',
    border: `1px solid ${theme.palette.common.black}`,
    borderRadius: '1px',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    '&:hover': {
      cursor: 'pointer',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
};

type AddProductOrServiceProps = {
  brUid?: string | null;
  partnerId?: string | null;
  accountNumber?: string | null;
};

export const AddProductOrService = ({
  brUid: brUidRaw,
  partnerId: partnerIdRaw,
  accountNumber: accountNumberRaw,
}: AddProductOrServiceProps) => {
  const brUid = brUidRaw ?? undefined;
  const partnerId = partnerIdRaw ?? undefined;
  const accountNumber = accountNumberRaw ?? undefined;
  const dispatch = useDispatch<AppDispatch>();
  const TITLE = 'Add Products / Services';
  const SEARCH_BAR_TITLE = 'Search Products / Services';
  const navigate = useNavigate();
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const redirectOnSelection = useRef<boolean>(false);
  const creationInProgress = useRef<boolean>(false);
  const loaderWasActiveDuringCreation = useRef<boolean>(false);
  const [hover, setHover] = useState<{ key: string; isHovering: boolean }>({
    key: '',
    isHovering: false,
  });
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [isCreatingServiceAgreement, setIsCreatingServiceAgreement] = useState<boolean>(false);
  const { loader } = useSelector((state: RootState) => state.appConfig);
  const isProductCreationDisabled: boolean = isCreatingServiceAgreement || loader.loading;

  const handleProductClick = (product: ProductCatalogueResponse): void => {
    if (creationInProgress.current) {
      return;
    }

    creationInProgress.current = true;
    setIsCreatingServiceAgreement(true);
    const serviceId = product.uid ?? '';
    dispatch(createServiceAgreement(partnerId, brUid, serviceId));
    redirectOnSelection.current = true;
  };

  useEffect(() => {
    if (!isCreatingServiceAgreement) {
      return;
    }

    if (loader.loading) {
      loaderWasActiveDuringCreation.current = true;
      return;
    }

    if (loaderWasActiveDuringCreation.current) {
      creationInProgress.current = false;
      loaderWasActiveDuringCreation.current = false;
      setIsCreatingServiceAgreement(false);
    }
  }, [isCreatingServiceAgreement, loader.loading]);

  useEffect(() => {
    if (!redirectOnSelection.current || !selectedServiceAgreement) {
      return;
    }

    if (partnerId) {
      if (selectedServiceAgreement?.agreementStatus === 'READY_FOR_CONFIGURATION') {
        navigate(`/partner/${partnerId}/serviceAgreement/${selectedServiceAgreement?.processInstanceId}/configuration`);
      } else {
        navigate(`/partner/${partnerId}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
      }
    } else if (brUid) {
      if (
        selectedServiceAgreement?.agreementStatus === 'READY_FOR_CONFIGURATION' ||
        selectedServiceAgreement?.agreementStatus === 'IN_CONFIGURATION'
      ) {
        navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.processInstanceId}/configuration`);
      } else {
        navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
      }
    }

    redirectOnSelection.current = false;
  }, [selectedServiceAgreement, partnerId, brUid, accountNumber, dispatch, navigate]);

  useEffect(() => {
    if (!brUid && !partnerId) {
      return;
    }
    dispatch(getProductsThunk(brUid, partnerId));
  }, [brUid, dispatch, partnerId]);

  const onSearchFieldChange = (text: string): void => {
    setSearchFieldValue(text);
  };

  const products = useSelector((state: GlobalState) => {
    return state.product.products;
  });

  let productsGroupedByFamily: { family: string; items: ProductCatalogueResponse[] }[] = _(products)
    .groupBy('family')
    .map((items, family) => ({ family, items }))
    .value();

  productsGroupedByFamily = productsGroupedByFamily.map((product) => {
    return {
      ...product,
      items: product.items.filter((item) => {
        return item.productName?.toLowerCase().includes(searchFieldValue.toLowerCase());
      }),
    };
  });

  return (
    <Box data-testid="add-products-services" sx={{ mb: 10 }}>
      <StickyHeaderWrapper
        hasStickyBottomBar={false}
        hasOpenTask={false}
        side="right"
        topComponents={
          !loader.loading && (
            <AccordionGridWrapper expandedProp={true} showExpandIcon={false} title={TITLE} customCloseAction={true}>
              <Grid container>
                <SimpleSearch onChange={onSearchFieldChange} searchValue={searchFieldValue} title={SEARCH_BAR_TITLE} />
              </Grid>
            </AccordionGridWrapper>
          )
        }
        centerComponents={
          loader.loading ? (
            <LoadingSpinner message={loader.message} />
          ) : productsGroupedByFamily.length > 0 ? (
            productsGroupedByFamily.map((product, productIndex) => {
              return (
                <AccordionGridWrapper key={productIndex} title={product.family}>
                  <Grid container sx={{ display: 'flex', mb: 1 }}>
                    {product.items.map((item, index) => {
                      return (
                        <Grid
                          onMouseEnter={() =>
                            setHover({
                              key: item.productName ?? '-',
                              isHovering: true,
                            })
                          }
                          onMouseLeave={() => setHover({ key: '', isHovering: false })}
                          item
                          xs={4}
                          key={index * 10}
                        >
                          <Tooltip
                            title={item.shortDescription}
                            PopperProps={{
                              sx: {
                                '& .MuiTooltip-tooltip': {
                                  cursor: 'default',
                                  padding: '1em',
                                  fontSize: 14,
                                  backgroundColor: 'black',
                                  color: theme.palette.common.white,
                                },
                              },
                            }}
                          >
                            <Box
                              component="button"
                              type="button"
                              data-testid={`product-button-${item.productName}`}
                              disabled={isProductCreationDisabled}
                              sx={styles.productBox}
                              onClick={() => handleProductClick(item)}
                            >
                              <Grid
                                container
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                }}
                              >
                                <Grid item xs={11}>
                                  <Typography
                                    sx={{
                                      fontWeight: 'bold',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {item.productName}
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={1}
                                  sx={{
                                    p: 0,
                                    display: 'flex',
                                    aligItems: 'center',
                                    alignItems: 'center',
                                    justifyItems: 'center',
                                  }}
                                >
                                  {hover.isHovering && hover.key === item.productName ? (
                                    <AddCircleOutlineIcon
                                      sx={{
                                        backgroundColor: jbColors.core.reflexBlue[100],
                                        color: 'white',
                                        borderRadius: '50%',
                                      }}
                                    />
                                  ) : (
                                    <AddCircleOutlineIcon />
                                  )}
                                </Grid>
                              </Grid>
                              <Grid container sx={{ mt: 0.5 }}>
                                <Grid item xs={12}>
                                  <Typography
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      fontSize: '12px',
                                      lineHeight: '18px',
                                      letterSpacing: '0.4px',
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {item.shortDescription}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionGridWrapper>
              );
            })
          ) : (
            <Grid
              container
              sx={{
                flexGrow: 1,
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
              }}
            >
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, maxWidth: '30em' }}>
                No eligible products or services found for this BR considering type and main domicile of the account
                holder, BR type and booking center.
              </Typography>
            </Grid>
          )
        }
      />
    </Box>
  );
};
